import { DatePicker } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useId } from 'react';
import { inforOrangeIcon, calendarIcon, clearIcon } from '../assets';
import '../style.scss';
import type { DatePickerInputProps } from './types';

// WCAG "visually hidden" pattern: kept in the accessibility tree (unlike display:none) so
// htmlFor still resolves to a real accessible name when no visible label is given.
const VISUALLY_HIDDEN_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const DatePickerInput = ({
  className = undefined,
  label = '',
  ariaLabel = '',
  name = undefined,
  placeholder = '',
  isError = false,
  errorMessage = '',
  isWarning = false,
  warningMessage = false as unknown as string,
  value = undefined,
  belowLimit = undefined,
  aboveLimit = undefined,
  disabled = false,
  suffixIcon = undefined,
  allowClear = undefined,
  getPopupContainer = undefined,
  inputReadOnly = false,
  id,
  onChange,
}: DatePickerInputProps) => {
  const generatedId = useId();
  const resolvedId = id || generatedId;

  return (
    <div className={classNames(className, 'date-picker-input')}>
      {label ? (
        <label htmlFor={resolvedId} className="date-picker-label">
          {label}
        </label>
      ) : (
        !!ariaLabel && (
          <label htmlFor={resolvedId} style={VISUALLY_HIDDEN_STYLE}>
            {ariaLabel}
          </label>
        )
      )}
      <DatePicker
        id={resolvedId}
        className={classNames(isError ? 'error' : '', disabled ? 'disabled' : '')}
        placeholder={placeholder}
        disabledDate={(currentDate) => {
          const currentDateStartOfDay = dayjs(currentDate.format('MM/DD/YYYY')).startOf('day');
          const currentDateEndOfDay = dayjs(currentDate.format('MM/DD/YYYY')).endOf('day');
          return (
            (belowLimit !== undefined && currentDateStartOfDay.valueOf() < belowLimit) ||
            (aboveLimit !== undefined && currentDateEndOfDay.valueOf() > aboveLimit)
          );
        }}
        name={name}
        value={value}
        onChange={onChange}
        format="MM/DD/YYYY"
        disabled={disabled}
        suffixIcon={suffixIcon || <img src={calendarIcon} alt="Suffix icon" />}
        allowClear={allowClear}
        getPopupContainer={getPopupContainer}
        clearIcon={<img src={clearIcon} alt="Clear icon" />}
        inputReadOnly={inputReadOnly}
      />
      {isError && !!errorMessage && <div className="date-picker-error-message">{errorMessage}</div>}
      {isWarning && (
        <div className="date-picker-input__warning-container">
          <img src={inforOrangeIcon} alt="inforOrangeIcon" />
          <div className="date-picker-input__warning-message">{warningMessage}</div>
        </div>
      )}
    </div>
  );
};

export default DatePickerInput;
